import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { format, endOfDay } from 'date-fns';
import { orderBy } from 'lodash';

import Table from '../../components/table/Table';
import TBody from '../../components/table/TBody/TBody';
import TD from '../../components/table/TD/TD';
import TR from '../../components/table/TR/TR';
import THead from '../../components/table/THead/THead';
import TH from '../../components/table/TH/TH';
import CollapsiblePanel from '../components/collapsible-panel/CollapsiblePanel';
import { getBarrier, getBarrierLogs, getBarriers } from '../../core/state/barriers/asyncActions';
import Input from '../../components/form/input/Input';
import Select from '../../components/form/select/Select';
import Badge from '../components/badge/Badge';
import Icon from '../../components/icon/Icon';
import { RootState } from '../../core/state/state';
import { getBarrierModeColor, getDeliveryZonesBadges } from '../barrier-management/helpers';
import { DATE_FORMAT_ISO, TIME_FORMAT_BACKEND, DATE_FORMAT_EU_DOT, DATE_TIME_FORMAT_WITH_TIMEZONE } from '../../core/constants/date';
import deliveryZoneColorMap from '../../core/constants/deliveryZoneColorMap';
import InteractiveElement from '../../components/interactive-element/InteractiveElement';
import { BarrierLogType } from '../../core/state/barriers/types';

import './BarrierLogs.scss';

type FormData = {
  date: string;
  event: BarrierLogType | string;
};

const BarrierLogs: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { barrierId } = useParams<{ barrierId: string }>();
  const barrier = useSelector((state: RootState) => state.barriers.barrier);
  const barrierLogs = useSelector((state: RootState) => state.barriers.barrierLogs);

  const [isAscending, setIsAscending] = React.useState(true);

  React.useEffect(() => {
    dispatch(getBarrier(Number(barrierId)));
  }, [dispatch, barrierId]);

  const { control, watch } = useForm<FormData>({
    defaultValues: {
      date: format(new Date(), DATE_FORMAT_ISO),
      event: ''
    }
  });

  const barrierLogTypeOptions = useMemo(
    () => [
      {
        text: t('common.all'),
        value: ''
      },
      {
        text: t(`barrierLogs.option.${BarrierLogType.Alive}`),
        value: BarrierLogType.Alive
      },
      {
        text: t(`barrierLogs.option.${BarrierLogType.Access}`),
        value: BarrierLogType.Access
      },
      {
        text: t(`barrierLogs.option.${BarrierLogType.ConstructionSiteAssigned}`),
        value: BarrierLogType.ConstructionSiteAssigned
      },
      {
        text: t(`barrierLogs.option.${BarrierLogType.DeliveryZoneAssigned}`),
        value: BarrierLogType.DeliveryZoneAssigned
      }
    ],
    [t]
  );
  const selectedEvent = watch('event');
  const selectedDate = watch('date');

  React.useEffect(() => {
    const startDate = format(new Date(selectedDate), DATE_TIME_FORMAT_WITH_TIMEZONE);
    const endDate = format(endOfDay(new Date(selectedDate)), DATE_TIME_FORMAT_WITH_TIMEZONE);

    dispatch(getBarriers());
    dispatch(getBarrierLogs(Number(barrierId), startDate, endDate, selectedEvent));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent, selectedDate]);

  const displayDeliveryZone = () => {
    if (barrier && barrier.locations[0].deliveryZoneIndex === null) {
      return '--';
    }

    return (
      barrier &&
      (barrier.locations.length < 6
        ? barrier.locations.map((item) => (
            <div className="pl-1" key={item.deliveryZoneIndex}>
              <Badge color={deliveryZoneColorMap[item.deliveryZoneIndex]} icon={item.deliveryZoneIndex} label={item.deliveryZoneName} />
            </div>
          ))
        : getDeliveryZonesBadges(barrier.locations))
    );
  };

  return (
    <div className="barrier-logs">
      <div className="barrier-logs__inner">
        <CollapsiblePanel title={t('barrierLogs.details')}>
          <div className="barrier-logs__details">
            <div className="barrier-logs__row font-weight-bold">
              <span>{t('barrierManagement.heading.snControl')}</span>
              <span>{t('barrierManagement.heading.snSystem')}</span>
              <span>{t('barrierManagement.heading.inventoryNumber')}</span>
              <span>{t('barrierLogs.projectAssignment')}</span>
            </div>
            <div className="barrier-logs__row">
              <span>{barrier?.serialNumber}</span>
              <span>{barrier?.barrierGateSerialNumber ?? '--'}</span>
              <span>{barrier?.inventoryNumber ?? '--'}</span>
              <span>{barrier?.locations[0].label ?? '--'}</span>
            </div>
            <div className="barrier-logs__row font-weight-bold">
              <span>{t('booking.deliveryZone')}</span>
              <span>{t('common.status')}</span>
              <span>{t('common.description')}</span>
            </div>
            <div className="barrier-logs__row">
              <span>
                <div>
                  <div className="d-flex">{displayDeliveryZone()}</div>
                </div>
              </span>
              <span>
                <Badge color={getBarrierModeColor(barrier?.mode)} label={barrier?.mode} />
              </span>
              <span>{barrier?.description !== '' ? barrier?.description : '--'}</span>
            </div>
          </div>
        </CollapsiblePanel>
        <div className="barrier-logs__toolbar">
          <form className="d-flex">
            <Controller
              name="event"
              control={control}
              render={({ field: { name, value, onChange } }) => (
                <Select
                  id="event"
                  name={name}
                  className="barrier-logs__select mr-2"
                  options={barrierLogTypeOptions}
                  showSearch={false}
                  value={value ?? ''}
                  onChange={(option) => {
                    if (Array.isArray(option)) {
                      [option] = option; // eslint-disable-line no-param-reassign
                    }
                    onChange(option.value === 'All' ? '' : option.value);
                  }}
                />
              )}
            />
            <Controller
              name="date"
              control={control}
              render={({ field: { name, value, onChange } }) => (
                <Input
                  id="date"
                  className="barrier-logs__date ml-3"
                  type="date"
                  name={name}
                  value={value}
                  onChange={(e) => {
                    onChange(e.target.value);
                  }}
                />
              )}
            />
          </form>
        </div>

        <div className="barrier-logs__table">
          <Table>
            <THead>
              <TR>
                <TH>{t('barrierLogs.heading.logType')}</TH>
                <TH>{t('barrierLogs.heading.date')}</TH>
                <TH>
                  <InteractiveElement className="align-items-center d-flex" onClick={() => setIsAscending(!isAscending)}>
                    <span className="pr-2">{t('barrierLogs.heading.time')}</span>
                    <Icon className={`${isAscending ? 'barrier-logs__arrow--desc' : ''}`} icon="arrow" svg size="extra-small" />
                  </InteractiveElement>
                </TH>
                <TH>{t('barrierLogs.heading.pin')}</TH>
                <TH>{t('barrierLogs.heading.result')}</TH>
                <TH>{t('barrierLogs.heading.response')}</TH>
              </TR>
            </THead>
            <TBody>
              {orderBy(barrierLogs, (item) => new Date(item.logDateTimeUtc), [isAscending ? 'asc' : 'desc']).map((item) => (
                <TR key={item.logDateTimeUtc}>
                  <TD>{t(`barrierLogs.option.${item.type}`)}</TD>
                  <TD>{format(new Date(item.logDateTimeUtc), DATE_FORMAT_EU_DOT)}</TD>
                  <TD>{format(new Date(item.logDateTimeUtc), TIME_FORMAT_BACKEND)}</TD>
                  <TD>{item.pin}</TD>
                  <TD>
                    <Icon svg className={`barrier-logs__icon ${item.result ? '' : 'barrier-logs__icon--red'}`} icon="tick" size="extra-small" />
                  </TD>
                  <TD>{item.resultDetails}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default BarrierLogs;
